import React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import NewShowCard from "./NewShowCard";
import SectionTitle from "./ui/SectionTitle";
import { cn } from "../utils/classNames";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const NewShowsGrid = React.forwardRef(
  ({
    releases,
    loading = false,
    className,
    showTitle = true,
    title = "Upcoming & New Seasons",
    density = "comfortable",
    recommendationVariant = "none",
    highlightedShowId = null,
    isInWatchlist,
    onToggleWatchlist,
    ...props
  }, ref) => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const getEntryProps = (index) => ({
      // Keep SSR/prerender content visible for crawlers; avoid hidden initial states.
      initial: false,
      animate: { opacity: 1, y: 0 },
      transition: {
        duration: 0.25,
        ease: [0.0, 0.0, 0.2, 1],
        delay: prefersReducedMotion ? 0 : index * 0.05,
      },
    });

    if (loading) {
      return (
        <div className={cn("space-y-6", className)} ref={ref} {...props}>
          {showTitle && (
            <SectionTitle variant="h2">
              {title}
            </SectionTitle>
          )}
          
          {/* Loading skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, index) => (
              <div 
                key={index} 
                className="bg-card rounded-xl border border-accent-gold/70 overflow-hidden animate-pulse"
              >
                <div className="aspect-[2/3] bg-muted" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-muted rounded" />
                  <div className="h-3 bg-muted rounded w-3/4" />
                  <div className="flex gap-2">
                    <div className="h-6 bg-muted rounded-full w-16" />
                    <div className="h-6 bg-muted rounded-full w-12" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-muted rounded" />
                    <div className="h-3 bg-muted rounded" />
                    <div className="h-3 bg-muted rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (!releases || releases.length === 0) {
      return (
        <div className={cn("space-y-6", className)} ref={ref} {...props}>
          {showTitle && (
            <SectionTitle variant="h2">
              {title}
            </SectionTitle>
          )}
          
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-body font-medium text-foreground mb-2">
              No shows found
            </h3>
            <p className="text-body-small text-muted-foreground max-w-sm mx-auto">
              Try adjusting your filters or check back later for new releases.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={cn("space-y-6", className)} ref={ref} {...props}>
        {showTitle && (
          <SectionTitle variant="h2">
            {title}
          </SectionTitle>
        )}
        
        <div
          className={cn(
            "grid grid-cols-1",
            density === "compact"
              ? "md:grid-cols-2 lg:grid-cols-3 gap-4"
              : "md:grid-cols-2 lg:grid-cols-3 gap-8"
          )}
        >
          {releases.map((release, index) => (
            <motion.div key={release.id} className="h-full" {...getEntryProps(index)}>
              <NewShowCard
                release={release}
                density={density}
                recommendationVariant={recommendationVariant}
                priority={index < 8}
                highlighted={String(release.id) === highlightedShowId}
                isInWatchlist={isInWatchlist}
                onToggleWatchlist={onToggleWatchlist}
              />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }
);

NewShowsGrid.displayName = "NewShowsGrid";

NewShowsGrid.propTypes = {
  releases: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      rating: PropTypes.number,
      poster: PropTypes.string,
      seasonNumber: PropTypes.number,
      releaseDate: PropTypes.string.isRequired,
      sourceName: PropTypes.string,
      year: PropTypes.number,
      endYear: PropTypes.number,
      genres: PropTypes.arrayOf(PropTypes.string),
      networks: PropTypes.arrayOf(PropTypes.string),
      plotOverview: PropTypes.string,
      detailsUrl: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  className: PropTypes.string,
  showTitle: PropTypes.bool,
  title: PropTypes.string,
  density: PropTypes.oneOf(["comfortable", "compact"]),
  recommendationVariant: PropTypes.oneOf(["none", "poster-ribbon"]),
  highlightedShowId: PropTypes.string,
  isInWatchlist: PropTypes.func,
  onToggleWatchlist: PropTypes.func,
};

export default NewShowsGrid;
